import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getCanvas } from 'src/utils/mapUtils';

export interface LayerProperty {
  color: string | null;
  opacity?: number;
  weight?: number; // For lines
  radius?: number; // For points
  fillColor?: string; // For polygons
  fillOpacity?: number; // For polygons
  dashArray?: 'solid' | 'dashed' | 'dotted'; // For lines
  markerShape?: 'circle' | 'square' | 'triangle' | 'pin'; // For points
}

export interface GISLayer {
  id: string;
  kind: 'Point' | 'Polygon' | 'LineString' | 'Overlay';
  config: LayerProperty;
}

interface GeomLayersState {
  visibleLayers: string[];
  loading: boolean;
  error: string | null;
}

const initialState: GeomLayersState = {
  visibleLayers: [],
  loading: false,
  error: null,
};

/*
 * Return the React Query key for the geometry layers
 */
export const getLayersQueryKey = (projectId) => {
  return ['gisLayers', projectId];
};

const geomLayerSlice = createSlice({
  name: 'geomLayers',
  initialState,
  reducers: {
    showGeomLayer: (
      state,
      action: PayloadAction<{
        layerId: string;
      }>
    ) => {
      const { layerId } = action.payload;
      state.visibleLayers = [...state.visibleLayers, layerId];
    },

    hideAll: (state, action) => {
      state.visibleLayers = [];
    },

    hideGeomLayer: (state, action: PayloadAction<{ layerId: string }>) => {
      const { layerId } = action.payload;
      const newVisible = state.visibleLayers.filter((id) => id !== layerId);
      state.visibleLayers = newVisible;
    },

    hideGeomLayers: (
      state,
      action: PayloadAction<{
        layerIds: string[];
      }>
    ) => {
      const { layerIds } = action.payload;
      state.visibleLayers = state.visibleLayers.filter(
        (id) => layerIds.indexOf(id) < 0
      );
    },
  },
});

export const { showGeomLayer, hideAll, hideGeomLayer, hideGeomLayers } =
  geomLayerSlice.actions;

export default geomLayerSlice.reducer;

/**
 * Helper function to check if a websocket message is for GIS layer creation
 */
export function isGISLayerCreation(data) {
  return (
    data?.objects &&
    data.objects.length > 0 &&
    data.objects[0].kind &&
    ['Point', 'LineString', 'Polygon', 'Overlay'].includes(data.objects[0].kind)
  );
}

/**
 * Helper function to handle GIS layer deletions - hides from map
 */
export function handleGISLayerDeletions(deletedIds) {
  const canvas = getCanvas();
  const gis = canvas?.gis;
  if (gis && deletedIds && deletedIds.length > 0) {
    gis.hideLayers(deletedIds);
  }
}
